import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { apiLoadErrorText, useListPrcLimitsQuery, type PrcVersionPayload } from '@shared/api';
import { Input } from '@shared/ui/Input';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { IconSearch } from '@shared/ui/icons';
import { ScrollRegion, fleetLeading, fleetType, formatCount, nameKey, typo } from '@entities/fleet';
import { compareDecimal, subtractDecimal, toDecimalText } from '@entities/prc';
import { Notice, TextButton } from '@widgets/FleetRegistry/ui';
import type { PrcAccess } from '../access';
import { formatNumber, formatPercent } from '../format';
import { NARROW, NumTable, SegmentButton, Segmented, Toolbar } from '../ui';
import { LimitsDrawer } from './LimitsDrawer';

const Search = styled.div`
  position: relative;
  flex: 1 1 240px;
  min-width: 0;
  max-width: 360px;

  & > svg {
    position: absolute;
    left: ${({ theme }) => theme.spacing(3)};
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.text.secondary};
    pointer-events: none;
  }

  @container prc (max-width: ${NARROW}) {
    max-width: none;
    flex-basis: 100%;
  }
`;

const SearchInput = styled(Input)`
  padding-left: ${({ theme }) => theme.spacing(9)};
`;

const Intro = styled.p`
  margin: ${({ theme }) => `${theme.spacing(3)} 0 0`};
  max-width: 80ch;
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-wrap: pretty;
`;

const Controls = styled(Toolbar)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const TableBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Table = styled(NumTable)`
  td.c-name {
    min-width: 260px;
  }

  tr.row {
    cursor: pointer;
  }

  .narrow-only {
    display: none;
  }

  @container prc (max-width: ${NARROW}) {
    &,
    & > tbody,
    & tr,
    & td {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    .narrow-only {
      display: block;
    }

    tr.row {
      padding: ${({ theme }) => `${theme.spacing(3)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    tr.row > td {
      padding: 0;
      border: none;
      background: none;
    }
  }
`;

const NameButton = styled.button`
  display: block;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: ${fleetType.base};
  font-weight: 500;
  line-height: ${fleetLeading.tight};
  text-align: left;
  overflow-wrap: anywhere;
  cursor: pointer;

  tr.row:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Meta = styled.span`
  display: block;
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: ${fleetType.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

type Show = 'with' | 'all';

/** Отклонение нормы по перечню от паспорта, п. п. — со знаком. */
function deviation(norm: string, passport: unknown): string | null {
  const p = toDecimalText(passport);
  return p === null ? null : subtractDecimal(norm, p);
}

/**
 * Вкладка «Лимиты на ремонт»: у каких моделей норма ремонта и ТО считается по
 * перечню запчастей и работ, и насколько она расходится с паспортом. Модель
 * открывает перечень целиком.
 */
export function LimitsTab({
  payload,
  access,
  openModelId,
  onOpenModel,
  onCloseModel,
}: {
  payload: PrcVersionPayload;
  access: PrcAccess;
  openModelId: string | null;
  onOpenModel: (id: string) => void;
  onCloseModel: () => void;
}) {
  const v = payload.version;
  const { data, error, isLoading, refetch } = useListPrcLimitsQuery(v.id);
  const items = data?.items ?? [];
  const withLimits = items.filter((i) => i.has_limits).length;
  const [show, setShow] = useState<Show>('with');
  const [search, setSearch] = useState('');
  const effectiveShow: Show = withLimits === 0 ? 'all' : show;

  const visible = useMemo(() => {
    const q = nameKey(search);
    return items.filter(
      (i) => (effectiveShow === 'all' || i.has_limits) && (!q || nameKey(i.model_name).includes(q)),
    );
  }, [items, effectiveShow, search]);

  return (
    <>
      <Intro>
        {typo(
          'Перечень запчастей и работ модели: ресурс, количество в год, цена → годовая сумма. Итог ÷ стоимость машины × 100 = норма затрат на ремонт и ТО. Если перечень есть, ПРЦ считает ремонт по нему, а не по норме паспорта.',
        )}
        {payload.variant &&
          ` ${typo(`Перечни общие для основного расчёта и всех вариантов: правка здесь пересчитает и вариант «${payload.variant.name}».`)}`}
      </Intro>
      {error !== undefined && !data ? (
        <TableBox>
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(
                error,
                'Не удалось загрузить перечни лимитов.',
                'Нет доступа к ПРЦ.',
              )}
            </span>
            <TextButton type="button" onClick={() => void refetch()}>
              Повторить
            </TextButton>
          </Notice>
        </TableBox>
      ) : isLoading ? (
        <TableBox aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => (
            <SkeletonLine key={i} $height={20} />
          ))}
        </TableBox>
      ) : (
        <>
          <Controls>
            <Search role="search">
              <IconSearch size={16} />
              <SearchInput
                type="search"
                value={search}
                placeholder="Модель"
                aria-label="Поиск модели"
                autoComplete="off"
                onChange={(e) => setSearch(e.target.value)}
              />
            </Search>
            <Segmented role="group" aria-label="Какие модели показать">
              <SegmentButton
                type="button"
                aria-pressed={effectiveShow === 'with'}
                disabled={withLimits === 0}
                onClick={() => setShow('with')}
              >
                С перечнем · {formatCount(withLimits)}
              </SegmentButton>
              <SegmentButton
                type="button"
                aria-pressed={effectiveShow === 'all'}
                onClick={() => setShow('all')}
              >
                Все модели · {formatCount(items.length)}
              </SegmentButton>
            </Segmented>
          </Controls>
          {withLimits === 0 && (
            <TableBox>
              <Notice $tone="info">
                <span className="text">
                  {typo(
                    'Перечней лимитов в версии нет: норма ремонта у всех моделей — из паспорта. Перечни приходят вместе с книгой ЭММ (лист «Блок Лимиты на ремонт»).',
                  )}
                </span>
              </Notice>
            </TableBox>
          )}
          <TableBox>
            <ScrollRegion label="Перечни лимитов на ремонт">
              <Table>
                <thead>
                  <tr>
                    <th scope="col">Модель</th>
                    <th scope="col" className="num">
                      Строк
                    </th>
                    <th scope="col" className="num">
                      Норма по перечню
                    </th>
                    <th scope="col" className="num">
                      Норма в паспорте
                    </th>
                    <th scope="col" className="num">
                      Разница, п. п.
                    </th>
                    <th scope="col" className="num">
                      Ошибки формул
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((i) => {
                    const info = i.repair_limits;
                    const diff = info ? deviation(info.norm_pct, info.passport_norm_pct) : null;
                    return (
                      <tr key={i.model_id} className="row" onClick={() => onOpenModel(i.model_id)}>
                        <td className="c-name">
                          <NameButton
                            type="button"
                            data-prc-limits={i.model_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenModel(i.model_id);
                            }}
                          >
                            {i.model_name}
                          </NameButton>
                          <Meta className="narrow-only">
                            {info
                              ? `по перечню ${formatPercent(info.norm_pct, 2)} · в паспорте ${formatPercent(info.passport_norm_pct, 2)} · строк ${i.rows ?? 0}`
                              : 'перечня нет — норма из паспорта'}
                          </Meta>
                        </td>
                        <td className="wide-only num">{i.rows ?? '—'}</td>
                        <td className="wide-only num strong">
                          {info ? formatPercent(info.norm_pct, 2) : '—'}
                        </td>
                        <td className="wide-only num">
                          {info ? formatPercent(info.passport_norm_pct, 2) : '—'}
                        </td>
                        <td className="wide-only num">
                          {diff === null
                            ? '—'
                            : compareDecimal(diff, '0') === 0
                              ? '0'
                              : formatNumber(diff, 2, 0, true)}
                        </td>
                        <td className="wide-only num">
                          {info && info.errors > 0 ? info.errors : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </ScrollRegion>
          </TableBox>
        </>
      )}
      {openModelId && (
        <LimitsDrawer
          payload={payload}
          access={access}
          modelId={openModelId}
          onClose={onCloseModel}
        />
      )}
    </>
  );
}
